import React, { useState, useEffect, useMemo } from "react";
import {
  doc, getDoc, setDoc, updateDoc, collection, onSnapshot, query, where, serverTimestamp,
} from "firebase/firestore";
import { firestore } from "../shared/FirebaseConfig";
import { useMyRosterStudent, joinClassWithCode } from "../shared/studentIdentity";
import { studentGrade } from "../shared/gradeModel";
import { locStyle } from "../shared/signoutLocations";
import { resolveEntrees, resolveDrinks, lunchSummary } from "../shared/lunchModel";
import { EMERGENCIES, selfCheckIn } from "../shared/emergencyData";
import { createSignOut, signInOne } from "../teacher/signoutData";
import LunchPicker from "../teacher/LunchPicker";
import "./student.css";

const LOCATIONS = ["Bathroom", "Water Fountain", "Nurse", "Library", "Office", "Other"];

function todayKeyLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const StudentDashboard = () => {
  const { loading, user, student, refresh } = useMyRosterStudent();

  const [classNames, setClassNames] = useState({});
  const [classDocs, setClassDocs] = useState({});
  const [selectedClassId, setSelectedClassId] = useState(null);
  const [location, setLocation] = useState("");
  const [customLocation, setCustomLocation] = useState("");
  // Whether the selected class's teacher lets students type a custom "Other"
  // destination. Defaults to true (the long-standing behavior).
  const [allowCustom, setAllowCustom] = useState(true);
  const [activeSignOut, setActiveSignOut] = useState(null);
  const [school, setSchool] = useState(null);
  const [attendance, setAttendance] = useState(undefined); // undefined=loading, null=absent, obj=present
  const [lunchPopup, setLunchPopup] = useState(false);
  const [emergency, setEmergency] = useState(null);
  const [accounted, setAccounted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState("");
  const [joining, setJoining] = useState(false);
  const [joinError, setJoinError] = useState("");

  const classes = useMemo(() => student?.enrolledClasses || [], [student]);
  const lunchEnabled = !!school?.lunchEnabled;

  useEffect(() => {
    if (classes.length && !selectedClassId) setSelectedClassId(classes[0]);
  }, [classes, selectedClassId]);

  // Class names/rooms (readable now that the student's uid is in enrolledStudents).
  useEffect(() => {
    if (!classes.length) return;
    let cancelled = false;
    (async () => {
      const names = {}; const docs = {};
      for (const cid of classes) {
        try {
          const snap = await getDoc(doc(firestore, "classes", cid));
          if (snap.exists()) { names[cid] = snap.data().className || cid; docs[cid] = snap.data(); }
        } catch { /* skip */ }
      }
      if (!cancelled) { setClassNames(names); setClassDocs(docs); }
    })();
    return () => { cancelled = true; };
  }, [classes]);

  // The selected class's teacher controls whether "Other" allows free typing.
  // Same-school users may read peer profiles, so this resolves for enrolled students.
  useEffect(() => {
    const teacherId = classDocs[selectedClassId]?.teacherId;
    if (!teacherId) { setAllowCustom(true); return; }
    let cancelled = false;
    getDoc(doc(firestore, "users", teacherId))
      .then((s) => { if (!cancelled) setAllowCustom(s.exists() ? s.data().allowCustomLocation !== false : true); })
      .catch(() => { if (!cancelled) setAllowCustom(true); });
    return () => { cancelled = true; };
  }, [classDocs, selectedClassId]);

  // School doc → lunch config (any signed-in user can read schools).
  useEffect(() => {
    if (!student?.schoolId) return;
    getDoc(doc(firestore, "schools", student.schoolId))
      .then((s) => setSchool(s.exists() ? s.data() : null))
      .catch(() => setSchool(null));
  }, [student]);

  // Live: is THIS student currently signed out?
  useEffect(() => {
    if (!user) return;
    const unsub = onSnapshot(
      query(collection(firestore, "signouts"), where("studentDocumentNumber", "==", user.uid)),
      (snap) => { const m = snap.docs[0]; setActiveSignOut(m ? { id: m.id, ...m.data() } : null); },
      (err) => console.error("sign-out listener:", err)
    );
    return () => unsub();
  }, [user]);

  // Live: today's attendance record for the selected class.
  useEffect(() => {
    if (!student?.id || !selectedClassId) return;
    setAttendance(undefined);
    const ref = doc(firestore, "classes", selectedClassId, "attendance", todayKeyLocal(), "students", student.id);
    const unsub = onSnapshot(
      ref,
      (snap) => setAttendance(snap.exists() ? snap.data() : null),
      (err) => { console.error("attendance listener:", err); setAttendance(null); }
    );
    return () => unsub();
  }, [student, selectedClassId]);

  // Live: is there an active emergency for my school?
  useEffect(() => {
    if (!student?.schoolId) return;
    const unsub = onSnapshot(
      query(collection(firestore, EMERGENCIES), where("schoolId", "==", student.schoolId), where("isActive", "==", true)),
      (snap) => { const d = snap.docs[0]; setEmergency(d ? { id: d.id, ...d.data() } : null); },
      (err) => console.error("emergency listener:", err)
    );
    return () => unsub();
  }, [student]);

  // Live: am I accounted for in the active emergency?
  useEffect(() => {
    if (!emergency?.id || !student?.id) { setAccounted(false); return; }
    const unsub = onSnapshot(
      doc(firestore, EMERGENCIES, emergency.id, "checkIns", student.id),
      (snap) => setAccounted(snap.exists()),
      (err) => console.error("checkIn listener:", err)
    );
    return () => unsub();
  }, [emergency, student]);

  const attendanceRef = () =>
    doc(firestore, "classes", selectedClassId, "attendance", todayKeyLocal(), "students", student.id);

  const doSelfCheckIn = async () => {
    if (!emergency?.id || !student || busy) return;
    setBusy(true);
    try { await selfCheckIn(emergency.id, student, user); }
    catch (err) { console.error("self check-in:", err); }
    finally { setBusy(false); }
  };

  const handleJoin = async () => {
    if (!code.trim() || joining) return;
    setJoining(true); setJoinError("");
    try {
      await joinClassWithCode(code, user);
      refresh(); // re-resolves identity; the not-found card unmounts on success
    } catch (e) {
      setJoinError(
        e.message === "invalid-code"
          ? "That code didn't work — double-check it with your teacher."
          : "Something went wrong. Try again."
      );
      setJoining(false);
    }
  };

  const checkIn = async () => {
    if (!selectedClassId || !student || busy) return;
    if (lunchEnabled) { setLunchPopup(true); return; }
    setBusy(true);
    try { await setDoc(attendanceRef(), { timestamp: serverTimestamp() }); }
    catch (err) { console.error("check-in:", err); }
    finally { setBusy(false); }
  };

  const saveLunch = async (lunch, editing) => {
    setBusy(true);
    try {
      const lunchData = { entree: lunch.entree || "", drink: lunch.drink || "", grade: studentGrade(student) || "" };
      if (editing) await updateDoc(attendanceRef(), { lunch: lunchData });
      else await setDoc(attendanceRef(), { timestamp: serverTimestamp(), lunch: lunchData });
    } catch (err) { console.error("save lunch:", err); }
    finally { setBusy(false); setLunchPopup(false); }
  };

  const handleSignOut = async () => {
    if (!selectedClassId || !location || !student || busy) return;
    setBusy(true);
    try {
      const cls = classDocs[selectedClassId] || {};
      await createSignOut({
        studentDocumentNumber: user.uid,
        studentName: student.studentName || `${student.firstName || ""} ${student.lastName || ""}`.trim(),
        idNumber: student.studentId || "",
        grade: studentGrade(student),
        schoolId: student.schoolId,
        teacherUID: cls.teacherId || (student.teachers || [])[0] || null,
        teacherName: "",
        classId: selectedClassId,
        className: cls.className || classNames[selectedClassId] || "",
        homeroom: cls.className || classNames[selectedClassId] || "",
        room: cls.roomNumber || "",
        time_out: new Date().toISOString(),
        location: location === "Other" ? (allowCustom ? (customLocation.trim() || "Other") : "Other") : location,
      });
      setLocation(""); setCustomLocation("");
    } catch (err) { console.error("Error signing out:", err); }
    finally { setBusy(false); }
  };

  const handleSignIn = async () => {
    if (!activeSignOut?.id || busy) return;
    setBusy(true);
    try { await signInOne(activeSignOut.id); setActiveSignOut(null); }
    catch (err) { console.error("Error signing in:", err); }
    finally { setBusy(false); }
  };

  if (loading) {
    return <div className="student-page"><div className="student-card">Loading…</div></div>;
  }

  if (!student) {
    return (
      <div className="student-page">
        <div className="student-card student-notfound">
          <h2>Join your class</h2>
          <p>Enter the class code your teacher gave you.</p>
          <input
            className="student-input student-code-input"
            value={code}
            onChange={(e) => { setCode(e.target.value.toUpperCase()); setJoinError(""); }}
            onKeyDown={(e) => { if (e.key === "Enter") handleJoin(); }}
            placeholder="CLASS CODE"
            autoCapitalize="characters"
            autoCorrect="off"
            autoComplete="off"
          />
          <button
            className="student-bigbtn"
            onClick={handleJoin}
            disabled={joining || !code.trim()}
          >
            {joining ? "Joining…" : "Join class"}
          </button>
          {joinError && <p className="student-join-error">{joinError}</p>}
          <p className="student-already">
            Already added by your teacher?{" "}
            <button className="student-link" onClick={refresh}>Refresh</button>
            {user?.email ? <span className="student-signed-as"><br />Signed in as {user.email}</span> : null}
          </p>
        </div>
      </div>
    );
  }

  const firstName = student.firstName || (student.studentName || "").split(" ")[0] || "there";
  const present = attendance != null && attendance !== undefined;

  return (
    <div className="student-page">
      <div className="student-card">
        <h2 className="student-welcome">Hi, {firstName}! 👋</h2>

        {/* Emergency — account for yourself (highest priority) */}
        {emergency && (
          <div className={`student-emergency ${emergency.isDrill ? "is-drill" : "is-real"}`}>
            <div className="student-emergency-head">
              {emergency.isDrill ? "🟠 This is a drill" : "🚨 Emergency"}
            </div>
            {accounted ? (
              <div className="student-emergency-ok">✅ You're accounted for. Stay with your teacher.</div>
            ) : (
              <button className="student-bigbtn student-emergency-btn" onClick={doSelfCheckIn} disabled={busy}>
                I'm safe — check me in
              </button>
            )}
          </div>
        )}

        {/* Attendance — check yourself in */}
        {selectedClassId && (
          <div className="student-attendance">
            {present ? (
              <div className="student-checkedin">
                <span>✅ You're checked in{attendance?.lunch ? ` · 🍽 ${lunchSummary(attendance.lunch)}` : ""}</span>
                {lunchEnabled && (
                  <button className="student-link" onClick={() => setLunchPopup(true)}>Change lunch</button>
                )}
              </div>
            ) : (
              <button className="student-bigbtn student-checkinbtn" onClick={checkIn} disabled={busy}>
                ✋ I'm here
              </button>
            )}
          </div>
        )}

        {activeSignOut ? (
          <div className="student-active">
            <div className="student-active-badge" style={{ background: locStyle(activeSignOut.location).bg, color: locStyle(activeSignOut.location).color }}>
              <span className="student-active-emoji">{locStyle(activeSignOut.location).emoji}</span>
              You're at the {activeSignOut.location}
            </div>
            <p className="student-active-time">
              Since {new Date(activeSignOut.time_out).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
            </p>
            <button className="student-bigbtn student-backbtn" onClick={handleSignIn} disabled={busy}>
              ✅ I'm back
            </button>
          </div>
        ) : (
          <>
            {classes.length > 1 && (
              <div className="student-section">
                <h3>Which class?</h3>
                <div className="student-class-tabs">
                  {classes.map((cid) => (
                    <button
                      key={cid}
                      className={`student-class-tab ${selectedClassId === cid ? "active" : ""}`}
                      onClick={() => setSelectedClassId(cid)}
                    >
                      {classNames[cid] || "Class"}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="student-section">
              <h3>Where are you going?</h3>
              <div className="student-tile-grid">
                {LOCATIONS.map((loc) => {
                  const s = locStyle(loc);
                  const active = location === loc;
                  return (
                    <button
                      key={loc}
                      className={`student-tile ${active ? "active" : ""}`}
                      style={active ? { borderColor: s.color, background: s.bg } : undefined}
                      onClick={() => { setLocation(loc); if (loc !== "Other") setCustomLocation(""); }}
                    >
                      <span className="student-tile-emoji">{s.emoji}</span>
                      <span className="student-tile-label">{loc}</span>
                    </button>
                  );
                })}
              </div>
              {location === "Other" && allowCustom && (
                <input
                  className="student-input"
                  placeholder="Where are you going?"
                  value={customLocation}
                  onChange={(e) => setCustomLocation(e.target.value)}
                />
              )}
            </div>

            <button
              className="student-bigbtn"
              onClick={handleSignOut}
              disabled={!location || !selectedClassId || busy}
            >
              🚪 Sign out
            </button>
          </>
        )}
      </div>

      {lunchPopup && (
        <LunchPicker
          studentName={firstName}
          entrees={resolveEntrees(school?.lunchEntrees)}
          drinks={resolveDrinks(school?.lunchDrinks)}
          initial={attendance?.lunch || null}
          editing={present}
          onSave={(lunch) => saveLunch(lunch, present)}
          onCancel={() => setLunchPopup(false)}
        />
      )}
    </div>
  );
};

export default StudentDashboard;
